import { Check } from 'lucide-react';
import { type FieldValues, useController } from 'react-hook-form';
import {
  ComboBox as AriaComboBox,
  Input as AriaInput,
  ListBox,
  ListBoxItem,
  Popover,
  Radio,
  RadioGroup,
  type Key,
} from 'react-aria-components';
import {
  Select,
  SelectContent,
  SelectDescription,
  SelectErrorMessage,
  SelectIndicator,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../tailgrids/core/select.js';
import { Spinner } from '../tailgrids/core/spinner/index.js';
import { HelpIcon } from '../ui/HelpIcon.js';
import { cn } from '../../utils/cn.js';
import { type EmptyAs, FormDescription, FormFieldError, type FormFieldBaseProps, FormLabel } from './field-parts.js';

export interface ChoiceOption {
  value: string | number;
  label: string;
  description?: string;
  disabled?: boolean;
}

const TRIGGER_CLASS = 'w-full border-border-secondary bg-input-background py-2.5';
const LIST_ITEM_CLASS =
  'group/item relative flex w-full cursor-pointer items-center gap-3 rounded-md py-1.5 pr-7 pl-2 text-sm text-text-secondary outline-hidden select-none focus:bg-background-gray-secondary_alt focus:text-text-primary data-disabled:cursor-not-allowed data-disabled:opacity-50';

function OptionText({ option }: { option: ChoiceOption }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate">{option.label}</span>
      {option.description && <span className="truncate text-xs text-text-tertiary">{option.description}</span>}
    </span>
  );
}

const disabledKeys = (options: ChoiceOption[]) => options.filter((o) => o.disabled).map((o) => o.value);

export interface SelectFormFieldProps<TValues extends FieldValues = FieldValues> extends FormFieldBaseProps<TValues> {
  options: ChoiceOption[];
  placeholder?: string;
  /** What clearing the selection writes (only reachable when the field is not required). */
  emptyAs?: EmptyAs;
}

export function SelectFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  help,
  required,
  disabled,
  className,
  options,
  placeholder = 'Choose…',
  emptyAs,
}: SelectFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  const value: Key | null = field.value === undefined || field.value === null ? null : field.value;
  return (
    <Select
      className={cn('w-full gap-1.5', className)}
      name={field.name}
      value={value ?? undefined}
      onChange={(next) => field.onChange(next === null ? emptyAs : next)}
      onBlur={field.onBlur}
      placeholder={placeholder}
      isInvalid={fieldState.invalid}
      isRequired={required}
      isDisabled={disabled || field.disabled}
      disabledKeys={disabledKeys(options)}
      validationBehavior="aria"
    >
      <FormLabel label={label} required={required} help={help} />
      <SelectTrigger ref={field.ref} className={TRIGGER_CLASS}>
        <SelectValue />
        <SelectIndicator />
      </SelectTrigger>
      <FormDescription>{description}</FormDescription>
      <FormFieldError message={fieldState.error?.message} />
      <SelectContent className="min-w-(--trigger-width)">
        {options.map((option) => (
          <SelectItem key={option.value} id={option.value} textValue={option.label}>
            <OptionText option={option} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface MultiSelectFormFieldProps<TValues extends FieldValues = FieldValues>
  extends FormFieldBaseProps<TValues> {
  options: ChoiceOption[];
  placeholder?: string;
}

/** Many values from a fixed list; the form holds an array of the chosen values. */
export function MultiSelectFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  required,
  disabled,
  className,
  options,
  placeholder = 'Choose…',
  help,
}: MultiSelectFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  const selected: Key[] = Array.isArray(field.value) ? field.value : [];
  const chosen = options.filter((option) => selected.includes(option.value));
  return (
    <Select
      selectionMode="multiple"
      className={cn('w-full gap-1.5', className)}
      name={field.name}
      value={selected}
      onChange={(next) => field.onChange(Array.isArray(next) ? next : [])}
      isInvalid={fieldState.invalid}
      isRequired={required}
      isDisabled={disabled || field.disabled}
    >
      <div className="flex items-center gap-1">
        <SelectLabel>
          {label}
          {required && <span className="ml-0.5 text-error-500">*</span>}
        </SelectLabel>
        {help && <HelpIcon text={help} />}
      </div>
      {description && <SelectDescription className="text-xs text-text-tertiary">{description}</SelectDescription>}
      <SelectTrigger ref={field.ref} onBlur={field.onBlur} className={cn(TRIGGER_CLASS, 'h-auto min-h-11 py-1.5')}>
        <span className="flex flex-1 flex-wrap items-center gap-1.5 text-left">
          {chosen.length === 0 ? (
            <span className="text-input-placeholder-text">{placeholder}</span>
          ) : (
            chosen.map((option) => (
              <span
                key={option.value}
                className="rounded-md bg-dropdown-hover-background px-2 py-0.5 text-sm font-medium text-text-primary"
              >
                {option.label}
              </span>
            ))
          )}
        </span>
        <SelectIndicator />
      </SelectTrigger>
      <SelectContent className="min-w-(--trigger-width)">
        {options.map((option) => (
          <SelectItem key={option.value} id={option.value} textValue={option.label} isDisabled={option.disabled}>
            <OptionText option={option} />
          </SelectItem>
        ))}
      </SelectContent>
      <SelectErrorMessage className="text-xs">{fieldState.error?.message}</SelectErrorMessage>
    </Select>
  );
}

export interface RadioGroupFormFieldProps<TValues extends FieldValues = FieldValues>
  extends FormFieldBaseProps<TValues> {
  options: ChoiceOption[];
  orientation?: 'vertical' | 'horizontal';
}

/** A few mutually exclusive choices that are worth showing at once (≤ 5). */
export function RadioGroupFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  help,
  required,
  disabled,
  className,
  options,
  orientation = 'vertical',
}: RadioGroupFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  return (
    <RadioGroup
      className={cn('flex flex-col gap-1.5', className)}
      name={field.name}
      value={field.value === undefined || field.value === null ? null : String(field.value)}
      onChange={(next) => field.onChange(options.find((o) => String(o.value) === next)?.value ?? next)}
      onBlur={field.onBlur}
      orientation={orientation}
      isInvalid={fieldState.invalid}
      isRequired={required}
      isDisabled={disabled || field.disabled}
      validationBehavior="aria"
    >
      <FormLabel label={label} required={required} help={help} />
      <div className={cn('flex gap-2', orientation === 'vertical' ? 'flex-col' : 'flex-wrap')}>
        {options.map((option) => (
          <Radio
            key={option.value}
            value={String(option.value)}
            isDisabled={option.disabled}
            className="group flex cursor-pointer items-start gap-2.5 rounded-lg border border-card-border px-3 py-2.5 text-sm text-text-secondary transition-colors data-disabled:cursor-not-allowed data-disabled:opacity-50 data-focus-visible:ring-4 data-focus-visible:ring-input-primary-focus-border/20 data-selected:border-primary-500 data-selected:text-text-primary"
          >
            <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-border-secondary-alt group-data-selected:border-[5px] group-data-selected:border-primary-500" />
            <OptionText option={option} />
          </Radio>
        ))}
      </div>
      <FormDescription>{description}</FormDescription>
      <FormFieldError message={fieldState.error?.message} />
    </RadioGroup>
  );
}

export interface ComboBoxFormFieldProps<TValues extends FieldValues = FieldValues> extends FormFieldBaseProps<TValues> {
  /** The options to offer; for a server search, the current results. */
  options: ChoiceOption[];
  placeholder?: string;
  /** Called as the person types — feed it to a search query and pass its results as `options`. */
  onSearchChange?: (text: string) => void;
  /** True while the search for the typed text is in flight. */
  loading?: boolean;
  emptyAs?: EmptyAs;
}

/** Choose one value from a long or searched list (entities, models, symbols). */
export function ComboBoxFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  help,
  required,
  disabled,
  className,
  options,
  placeholder = 'Search…',
  onSearchChange,
  loading = false,
  emptyAs,
}: ComboBoxFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  return (
    <AriaComboBox
      className={cn('flex w-full flex-col gap-1.5', className)}
      name={field.name}
      items={options}
      selectedKey={field.value === undefined || field.value === null ? null : field.value}
      onSelectionChange={(key) => field.onChange(key === null ? emptyAs : key)}
      onInputChange={onSearchChange}
      onBlur={field.onBlur}
      isInvalid={fieldState.invalid}
      isRequired={required}
      isDisabled={disabled || field.disabled}
      disabledKeys={disabledKeys(options)}
      menuTrigger="focus"
      validationBehavior="aria"
    >
      <FormLabel label={label} required={required} help={help} />
      <div className="relative">
        <AriaInput
          ref={field.ref}
          placeholder={placeholder}
          className="w-full rounded-lg border border-card-border bg-input-background px-3 py-2.5 text-sm text-title-50 outline-none placeholder:text-input-placeholder-text focus:border-input-primary-focus-border focus:ring-4 focus:ring-input-primary-focus-border/20 data-invalid:border-input-error-focus-border"
        />
        {loading && <Spinner size="sm" className="absolute top-1/2 right-3 -translate-y-1/2 text-icon-tertiary" />}
      </div>
      <FormDescription>{description}</FormDescription>
      <FormFieldError message={fieldState.error?.message} />
      <Popover className="w-(--trigger-width) overflow-auto rounded-lg border border-card-border bg-background-white-secondary shadow-md">
        <ListBox
          className="max-h-72 p-1.5 outline-none"
          renderEmptyState={() => (
            <p className="px-2 py-2 text-sm text-text-tertiary">{loading ? 'Searching…' : 'No matches'}</p>
          )}
        >
          {(option: ChoiceOption) => (
            <ListBoxItem id={option.value} textValue={option.label} className={LIST_ITEM_CLASS}>
              {({ isSelected }) => (
                <>
                  <OptionText option={option} />
                  {isSelected && <Check size={16} className="absolute right-2 text-primary-500" />}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaComboBox>
  );
}
